/**
 * Account Close Modal Component
 * Modal for confirming account closure with balance adjustment
 * Task 003c - Account Closing & Deletion Flow
 */

import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface AccountCloseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  accountName: string;
  currentBalance: number;
  isClosing?: boolean;
}

export const AccountCloseModal: React.FC<AccountCloseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  accountName,
  currentBalance,
  isClosing = false,
}) => {
  const { t } = useTranslation();
  const { formatMoney } = useUserPreferences();
  if (!isOpen) return null;

  const adjustmentAmount = Math.abs(currentBalance);
  const adjustmentType = currentBalance > 0 ? 'expense' : 'income';
  const willCreateTransaction = currentBalance !== 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {t('account_close_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {willCreateTransaction ? (
            <p className="font-mono text-sm text-[#1a1a1a]">
              {t('account_close_modal.message_with_adjustment', { 
                name: accountName, 
                type: adjustmentType === 'expense' ? '-' : '+',
                amount: formatMoney(adjustmentAmount)
              })}
            </p>
          ) : (
            <p className="font-mono text-sm text-[#1a1a1a]">
              {t('account_close_modal.message_simple', { name: accountName })}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '4px 4px 0 #077f5d'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
            disabled={isClosing}
          >
            {t('account_close_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-[2] px-4 py-3 bg-yellow-600 text-white border-2 border-yellow-800 font-bold uppercase disabled:opacity-50"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '6px 6px 0 #92400e'
            }}
            onMouseEnter={(e) => {
              if (!isClosing) {
                e.currentTarget.style.boxShadow = '4px 4px 0 #92400e';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '6px 6px 0 #92400e';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
            disabled={isClosing}
          >
            {willCreateTransaction ? (
              isClosing ? (
                t('account_close_modal.closing')
              ) : (
                t('account_close_modal.adjust_and_close')
              )
            ) : (
              isClosing ? t('account_close_modal.closing') : t('account_close_modal.close')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
